<template>
  <Layout>
    <div class="mc-invoice">
      <FormulateForm v-model="formValues" ref="formRef" class="mc-form" :errors="formErrors">
        <div class="mc-header__invoice-info d-flex">
          <h4 class="card-title flex-grow-1">{{ $t('invoice.title.generalInfo') }}</h4>
          <McButton
            @click="$refs['mc-modal-cancel'].show()"
            variant="secondary"
            class="mx-2"
            :text="$t('global.action.cancel')"
            icon="icon-outline_close"
          />
          <b-modal
            class="mc-modal"
            id="mc-modal-cancel"
            ref="mc-modal-cancel"
            :title="$t('global.action.cancel')"
            size="md"
            centered
            scrollable
          >
            <div class="card-body p-2">
              <p>{{ $t('global.text.modal.leavePage') }}?</p>
              <p>
                <b>{{ $t('global.confirmation.sure') }}</b>
              </p>
            </div>
            <template #modal-footer="{ cancel }">
              <div class="d-flex mx-0">
                <McButton
                  @click="handleExitPage"
                  variant="secondary"
                  class="me-2"
                  :text="$t('global.confirmation.exitPage')"
                />
                <McButton
                  @click="cancel()"
                  variant="primary"
                  :text="$t('global.confirmation.stayPage')"
                />
              </div>
            </template>
          </b-modal>

          <McButton
            variant="primary"
            type="submit"
            ref="save-button-ref"
            icon="icon-outline_save"
            :text="this.$t('global.save')"
            @click="handleSubmit"
          />
        </div>
        <b-card class="mc-invoice-info__card">
          <b-row>
            <b-col cols="12">
              <div class="text-black-50">{{ $t('invoice.fields.customer') }}</div>
              <div class="font-weight-bold">{{ customer?.name || '-' }}</div>
            </b-col>
          </b-row>
          <b-row class="g-0">
            <div class="mc-invoice-info__input-group">
              <div v-for="field in fields" :key="field.name">
                <FormulateInput
                  v-bind="field"
                  :ref="`${field.name}-ref`"
                  class="mc-invoice-info__input"
                  validation-behavior="live"
                  error-behavior="value"
                  :fullObject.sync="invoiceObj"
                  :help="field?.validation?.includes('required')"
                />
              </div>
            </div>
          </b-row>
          <b-row>
            <b-col cols="12" class="mt-3">
              <label class="mc-label">{{ $t('invoice.fields.infoLicensePlate') }}</label>
              <div class="d-flex">
                <McToggle
                  v-model="includeLicensePlate"
                  :label="$t('invoice.fields.includeLicensePlate')"
                  placement="left"
                />
                <div>
                  <i
                    id="license-plate-tooltip"
                    class="ps-2 mc-icon icon-fill_info"
                    v-b-tooltip.hover.righttop="$t('invoice.warningMessage.includeLicensePlate')"
                  />
                </div>
              </div>
            </b-col>
          </b-row>
        </b-card>
      </FormulateForm>

      <ServiceCost v-if="parentLoaded" :hasSalesOrder="true" />
      <Delivery />
      <ModalBranchList
        v-if="modalBranch"
        :value.sync="modalBranch"
        @submit="doSubmit"
        @cancel="doCancel"
      />
      <ModalNotificationV2
        :value.sync="modalWarning"
        :msg="warningMessage"
        :subMsg="subWarningMessage"
        :rejectedDos="rejectedDos"
        :selectedDos="selectedDos"
        :areAllCustomersSame="areAllCustomersSame"
        @handleContinue="handleContinue"
      />
    </div>
  </Layout>
</template>

<script>
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McToggle from '@/components/McToggle'
import { fields } from './lib/fields/generalInfoFields'
import ServiceCost from './components/ServiceCost'
import Delivery from './components/Delivery'
import moment from 'moment'
import { mapMutations, mapGetters } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import ModalBranchList from './components/Branch/ModalBranchList'
import * as Invoice from '@/services/rest-api/lib/invoice/invoice'
import { Helper } from '@/mixin/helper'
import errorMessageList from './lib/constants/error-message-mapper.js'
import ModalNotificationV2 from '../delivery/components/Provider/ModalNotificationV2'

export default {
  components: {
    Layout,
    McButton,
    McToggle,
    Delivery,
    ServiceCost,
    ModalBranchList,
    ModalNotificationV2,
  },
  mixins: [fields],
  data() {
    return {
      formValues: {},
      formErrors: {},
      invoiceObj: {},
      prevRoute: null,
      modalBranch: false,
      isSubmitting: false,
      modalWarning: false,
      warningMessage: '',
      subWarningMessage: '',
      selectedDos: [],
      rejectedDos: [],
      areAllCustomersSame: true,
      parentLoaded: false,
      includeLicensePlate: true,
      mappingErrorStatus: {
        DRAFT: 'Draf',
        CANCELED: 'Dibatalkan',
      },
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    customer() {
      return this.$store.state.invoice.customer
    },
  },
  watch: {
    'formValues.total_days': function (val) {
      const invoiceDate = this.formValues?.transaction_date
      if (invoiceDate && val) {
        const dueDate = moment(invoiceDate).add(val, 'days').format('YYYY-MM-DD')
        this.formValues['due_date'] = dueDate
        this.setGeneralInfo(this.formValues)
      } else {
        this.$delete(this.formValues, 'due_date')
      }
    },
    'formValues.transaction_date': function (val) {
      const totalDays = this.formValues?.total_days
      if (totalDays && val) {
        const dueDate = moment(val).add(totalDays, 'days').format('YYYY-MM-DD')
        this.formValues['due_date'] = dueDate
        this.setGeneralInfo(this.formValues)
      } else {
        this.$delete(this.formValues, 'due_date')
      }
    },
  },
  mounted() {
    if (!isEmpty(this.$store.state.invoice.generalInfo) && this.prevRoute?.name === null) {
      this.formValues = this.$store.state.invoice.generalInfo
    }

    if (this.prevRoute?.name !== null) {
      this.setGeneralInfo({})
      this.setProducts([])
    }
    this.parentLoaded = true
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevRoute = from
    })
  },
  methods: {
    ...mapMutations('invoice', ['setSelectedDos', 'setCustomer', 'setProducts', 'setGeneralInfo']),
    resetInvoiceForm() {
      this.setSelectedDos([])
      this.setGeneralInfo({})
      this.setCustomer({})
      this.setProducts([])
    },
    handleExitPage() {
      this.resetInvoiceForm()
      this.$router.back()
    },
    handleSubmit() {
      if (this.isSubmitting) return this.$mcToast.warning(this.$t('global.alert.text.pleaseWait'))

      if (this.$refs.formRef.hasErrors) {
        this.$mcToast.error(this.$t('setting.wrongFormat'))
        return
      }
      this.doSubmit()
    },
    async doSubmit(branchId = null) {
      const selectedDos = this.$store.state.invoice.selectedDos.map((v) => {
        return { id: v.id }
      })
      this.selectedDos = this.$store.state.invoice.selectedDos
      const items = this.$store.state.invoice.products.map((v) => {
        return {
          id: v.product.code,
          quantity: v.quantity,
          unit_price: v.unit_price,
          discount: v.discount,
          delivery_order_id: v.delivery_order.id,
          description: v.description,
        }
      })

      let payload = {
        delivery_orders: selectedDos,
      }

      if (branchId) {
        payload = {
          customer: { id: this.customer.number },
          items,
          delivery_orders: selectedDos,
          transaction_date: this.formValues.transaction_date,
          due_date: this.formValues.due_date,
          branch_id: branchId,
          send_fleet_data: this.includeLicensePlate,
        }
      }

      if (!items.length) {
        this.$mcToast.error(this.$t('invoice.warningMessage.itemIsRequired'))
        return
      }

      try {
        this.isSubmitting = true
        const { errors, message, errors_v2 } = await Invoice.store(payload)

        if (errors && typeof errors === 'object') {
          // Error unregistered vehicle
          const target = 'found unregistered vehicle'
          if (errors['items'] && errors['items'][0].search(target) !== -1) {
            const licensePlate = errors['items'][0].split(':')[1].trim()
            const errorMessage = this.$t(errorMessageList['items'][target], { licensePlate })
            this.$mcToast.error(errorMessage)
            this.isSubmitting = false
            return
          }

          /**
           * error example:
           * "errors": {
                "items[0].delivery_order_id": [
                  "delivery order id does not exist in the provided delivery orders"
                ]
              }
           */
          for (const errKey of Object.keys(errors)) {
            if (errKey.search('delivery_order_id') !== -1) {
              const errorMessage = this.$t(errorMessageList['items']['drlivery order not match'])
              this.$mcToast.error(errorMessage)
              this.isSubmitting = false
              return
            }
          }

          const errorKeys = ['invoice_id', 'status', 'external_reference', 'customer']
          let errorMessage = Helper.methods.withErrorHandler(
            { errors },
            errorMessageList,
            errorKeys,
            true
          )

          if (Array.isArray(errorMessage)) {
            this.warningMessage = this.$t('invoice.warningMessage.doStatusHasChanged')
            errorMessage.forEach((errorMessageItem) => {
              if (typeof errorMessageItem === 'string') {
                // are all customer same?
                if (
                  errors['delivery_orders'] &&
                  errors['delivery_orders'][0] === 'customer ids is not equal'
                ) {
                  this.modalWarning = true
                  this.warningMessage = errorMessageItem
                  this.areAllCustomersSame = false
                } else this.$mcToast.error(errorMessageItem)
                return
              }

              for (const [key, val] of Object.entries(errorMessageItem)) {
                if (['invoice_id', 'status'].includes(key)) {
                  this.modalWarning = true
                  this.rejectedDos = [...this.rejectedDos, ...errorMessageItem.id.split(',')]
                }

                // Update status DO, if DO status changed suddenly
                if (key === 'status') {
                  const indexOfDo = this.selectedDos.findIndex((v) => v.id === errorMessageItem.id)
                  if (indexOfDo !== -1) {
                    this.selectedDos[indexOfDo].status.name = this.mappingErrorStatus[val]
                  }
                }

                if (key === 'external_reference') {
                  this.$mcToast.error(errorMessageItem.errorMessage)
                  break
                }
              }
            })

            if (this.rejectedDos.length === this.selectedDos.length) {
              this.subWarningMessage = this.$t('global.alert.text.pleaseCheckAgain')
            }

            if (
              this.rejectedDos.length > 0 &&
              this.rejectedDos.length !== this.selectedDos.length
            ) {
              this.subWarningMessage = this.$t('delivery.invoice.warningMessage.continueWithoutDO')
            }
          }

          if (errors_v2) {
            const { errorsMessageHtml, extractedUrl } =
              Helper.methods.formatAccurateErrorMessages(errors_v2.issues)

            await this.$mcMessageDialog
              .error({
                title: 'Gagal Mengirim Faktur ke Accurate',
                subtitle: 'Pesan dari Accurate:',
                content: errorsMessageHtml,
                useHtml: true,
                secondaryButton: { text: 'Salin Link Instalasi', visible: !!extractedUrl },
              })
              .then(async (action) => {
                if (action === 'secondary' && extractedUrl) {
                  try {
                    await navigator.clipboard.writeText(extractedUrl)
                    this.$mcToast.success('Link instalasi berhasil disalin')
                  } catch (copyErr) {
                    console.error('Failed to copy:', copyErr)
                    this.$mcToast.error('Gagal menyalin link instalasi')
                  }
                }
              })
          }

          this.isSubmitting = false
          return
        }

        if (errors === true) {
          const errMsg = message
            ? this.$t(errorMessageList[message])
            : Helper.methods.withErrorHandler()
          this.$mcToast.error(errMsg)
          return
        }
      } catch (error) {
        this.isSubmitting = false
        this.$mcToast.error(Helper.methods.withErrorHandler())
        console.error(error)
        return
      }

      if (!branchId) {
        this.modalBranch = true
        return
      }

      this.$mcToast.success(
        this.$t('invoice.warningMessage.successCreateInvoice', { provider: 'ACCURATE' })
      )
      this.resetInvoiceForm()
      setTimeout(() => {
        this.$router.push({ name: 'Delivery' })
      }, 400)
    },
    doCancel() {
      this.isSubmitting = false
    },
    handleContinue() {
      this.modalWarning = false
      this.warningMessage = ''
      this.selectedDos = [...this.selectedDos.filter((v) => !this.rejectedDos.includes(v.id))]
      this.setSelectedDos(this.selectedDos)
      this.modalBranch = true
    },
  },
}
</script>
<style lang="scss" scoped>
.mc-invoice {
  .mc-header__invoice-info {
    margin-bottom: 12px;
  }
  .mc-header__invoice-info,
  .mc-invoice-info__card {
    display: inline-block;
    margin-top: 0px;
    width: 100%;
  }
  .mc-invoice-info__input-group {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-top: 12px;
    margin-bottom: 20px;
    justify-content: space-between;
  }
  .mc-assignment-info__input {
    width: 100%;
    padding: 0px;
  }
}
</style>
